/**
 * Audit trail.
 *
 * Built around one question an auditor actually asks: "who changed this, and
 * when?" So the row leads with WHEN and WHO, the verb is shown exactly as
 * recorded (an auditor quotes it; a paraphrase would not match the log), and
 * the before/after is a list of the fields that CHANGED rather than two blobs
 * of JSON to compare by eye.
 *
 * This screen is strictly read-only. There is nothing here to edit, redact or
 * delete, and the API behind it offers no way to.
 */

import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, qs } from '../lib/api';
import { useApiError } from '../lib/useApiError';
import { Card, Button, Pill, EmptyState, Skeleton, ErrorNotice } from '../components/ui';
import { IconShield, IconChevron } from '../components/icons';
import { cn } from '../lib/cn';

interface AuditEvent {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  actorId: string | null;
  actorEmail: string | null;
  ip: string | null;
  createdAt: string;
  changes: { before: unknown; after: unknown } | null;
}

interface AuditPage {
  items: AuditEvent[];
  total: number;
  page: number;
  pageSize: number;
}

interface Filters {
  actor: string;
  action: string;
  entityType: string;
  entityId: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = { actor: '', action: '', entityType: '', entityId: '', from: '', to: '' };
const PAGE_SIZE = 25;

/** Delay a fast-changing value, so typing in a box is not one request per key. */
function useDebounced<T>(value: T, ms = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

// ---------------------------------------------------------------------------
//  Diff
// ---------------------------------------------------------------------------

type FieldChange =
  | { path: string; kind: 'added'; after: unknown }
  | { path: string; kind: 'removed'; before: unknown }
  | { path: string; kind: 'changed'; before: unknown; after: unknown };

interface Diff {
  changes: FieldChange[];
  /** Fields recorded on only one side without being a change — see diffOf. */
  context: Array<{ path: string; value: unknown }>;
  unchangedCount: number;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** Flatten nested objects to `a.b.c` paths. Arrays are kept whole as values. */
function flatten(value: unknown, prefix = '', out: Map<string, unknown> = new Map(), depth = 0) {
  if (isObject(value) && depth < 6) {
    const entries = Object.entries(value);
    if (entries.length === 0 && prefix) out.set(prefix, value);
    for (const [k, v] of entries) flatten(v, prefix ? `${prefix}.${k}` : k, out, depth + 1);
  } else {
    out.set(prefix || '(value)', value);
  }
  return out;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Work out which fields changed.
 *
 * A subtlety that matters for accuracy: several update handlers record the
 * `before` as a snapshot of the record but the `after` as only the fields they
 * touched. A field present in `before` and absent from `after` therefore means
 * "not touched", NOT "removed" — reporting it as removed would tell an auditor
 * that a name or domain was deleted when it was not. Such fields are returned
 * as `context`, shown separately and never labelled as a change. Genuine
 * removal is reported only when there is no `after` at all (a deletion).
 */
function diffOf(before: unknown, after: unknown): Diff {
  const changes: FieldChange[] = [];
  const context: Diff['context'] = [];
  let unchangedCount = 0;

  const b = before === null || before === undefined ? null : flatten(before);
  const a = after === null || after === undefined ? null : flatten(after);

  if (!b && a) {
    for (const [path, value] of a) changes.push({ path, kind: 'added', after: value });
  } else if (b && !a) {
    for (const [path, value] of b) changes.push({ path, kind: 'removed', before: value });
  } else if (b && a) {
    for (const [path, value] of a) {
      if (!b.has(path)) changes.push({ path, kind: 'added', after: value });
      else if (!same(b.get(path), value)) changes.push({ path, kind: 'changed', before: b.get(path), after: value });
      else unchangedCount++;
    }
    for (const [path, value] of b) if (!a.has(path)) context.push({ path, value });
  }
  return { changes, context, unchangedCount };
}

const REDACTED = '[redacted]';
const MAX_VALUE_CHARS = 500;

/**
 * Render one value as text.
 *
 * Everything is emitted as a React text node, never as HTML: a recorded value
 * is user-supplied (a vendor name, a policy title) and must not be able to
 * inject markup into an auditor's screen.
 */
function Value({ value }: { value: unknown }) {
  const { t } = useTranslation();
  if (value === REDACTED) {
    return <span className="italic text-ink-muted">{t('audit.redacted')}</span>;
  }
  if (value === null || value === undefined || value === '') {
    return <span className="italic text-ink-muted">{t('audit.empty')}</span>;
  }
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return (
    <span className="break-words">
      {text.length > MAX_VALUE_CHARS ? `${text.slice(0, MAX_VALUE_CHARS)}…` : text}
    </span>
  );
}

function DiffView({ changes: raw }: { changes: NonNullable<AuditEvent['changes']> }) {
  const { t } = useTranslation();
  const [showContext, setShowContext] = useState(false);
  const diff = diffOf(raw.before, raw.after);

  if (diff.changes.length === 0 && diff.context.length === 0) {
    return <p className="oc-muted">{t('audit.noFieldChanges')}</p>;
  }

  const KIND_TONE = { added: 'good', removed: 'critical', changed: 'warning' } as const;

  return (
    <div className="space-y-2">
      {diff.changes.length > 0 && (
        <div className="oc-table-scroll">
          <table className="w-full text-sm border-separate border-spacing-y-1 min-w-[32rem]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-muted">
                <th scope="col" className="font-medium px-2 w-1/4">{t('audit.field')}</th>
                <th scope="col" className="font-medium px-2 w-[37%]">{t('audit.before')}</th>
                <th scope="col" className="font-medium px-2 w-[37%]">{t('audit.after')}</th>
              </tr>
            </thead>
            <tbody>
              {diff.changes.map((c) => (
                <tr key={c.path} className="align-top">
                  <th scope="row" className="px-2 py-1.5 text-left font-medium text-ink">
                    <span className="font-mono text-xs break-all">{c.path}</span>
                    <span className="block mt-1">
                      <Pill tone={KIND_TONE[c.kind]}>{t(`audit.kind.${c.kind}`)}</Pill>
                    </span>
                  </th>
                  <td className={cn('px-2 py-1.5 rounded-l-lg text-ink',
                    c.kind !== 'added' && 'bg-critical/[0.07]')}>
                    {c.kind === 'added' ? <span className="text-ink-muted">—</span> : <Value value={c.before} />}
                  </td>
                  <td className={cn('px-2 py-1.5 rounded-r-lg text-ink',
                    c.kind !== 'removed' && 'bg-good/[0.09]')}>
                    {c.kind === 'removed' ? <span className="text-ink-muted">—</span> : <Value value={c.after} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(diff.unchangedCount > 0 || diff.context.length > 0) && (
        <p className="text-xs text-ink-muted">
          {diff.unchangedCount > 0 && t('audit.unchangedHidden', { count: diff.unchangedCount })}{' '}
          {diff.context.length > 0 && (
            <button className="font-medium text-brand hover:underline" aria-expanded={showContext}
                    onClick={() => setShowContext((s) => !s)}>
              {showContext ? t('audit.hideContext') : t('audit.showContext', { count: diff.context.length })}
            </button>
          )}
        </p>
      )}

      {showContext && (
        <dl className="rounded-lg bg-ink/[0.04] px-3 py-2 text-xs grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-x-3 gap-y-1">
          {diff.context.map((c) => (
            <div key={c.path} className="contents">
              <dt className="font-mono break-all text-ink-secondary">{c.path}</dt>
              <dd className="text-ink"><Value value={c.value} /></dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Page
// ---------------------------------------------------------------------------

export function AuditTrailPage() {
  const { t, i18n } = useTranslation();
  const describeError = useApiError();

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);

  const set = (patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1); // a new filter can leave the current page number past the end
    setOpenId(null);
  };

  // Only the free-text boxes are debounced; selects and dates apply at once.
  const actor = useDebounced(filters.actor);
  const entityId = useDebounced(filters.entityId);
  const rangeInverted = !!filters.from && !!filters.to && filters.from > filters.to;

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['audit', { ...filters, actor, entityId, page }],
    queryFn: () =>
      api.get<AuditPage>(`/audit${qs({
        actor, entityId, action: filters.action, entityType: filters.entityType,
        from: filters.from, to: filters.to, page, pageSize: PAGE_SIZE,
      })}`),
    enabled: !rangeInverted, // the server would reject it; say so instead
    // Keep the old rows on screen while the next page loads, so paging and
    // typing do not flash the list empty.
    placeholderData: keepPreviousData,
  });

  const { data: options } = useQuery({
    queryKey: ['audit', 'filters'],
    queryFn: () => api.get<{ actions: string[]; entityTypes: string[] }>('/audit/filters'),
    staleTime: 60_000,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters = Object.values(filters).some(Boolean);

  const format = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(iso));

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header>
        <h1 className="oc-h1">{t('audit.title')}</h1>
        <p className="oc-muted mt-1">{t('audit.subtitle')}</p>
      </header>

      <Card className="!p-3">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <label className="block">
            <span className="oc-label">{t('audit.actor')}</span>
            <input className="oc-input" type="search" value={filters.actor}
                   placeholder={t('audit.actorPlaceholder')}
                   onChange={(e) => set({ actor: e.target.value })} />
          </label>
          <label className="block">
            <span className="oc-label">{t('audit.action')}</span>
            <select className="oc-input" value={filters.action}
                    onChange={(e) => set({ action: e.target.value })}>
              <option value="">{t('common.all')}</option>
              {(options?.actions ?? []).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="oc-label">{t('audit.entityType')}</span>
            <select className="oc-input" value={filters.entityType}
                    onChange={(e) => set({ entityType: e.target.value })}>
              <option value="">{t('common.all')}</option>
              {(options?.entityTypes ?? []).map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="oc-label">{t('audit.entityId')}</span>
            <input className="oc-input font-mono" type="search" value={filters.entityId}
                   placeholder={t('audit.entityIdPlaceholder')}
                   onChange={(e) => set({ entityId: e.target.value.trim() })} />
          </label>
          <label className="block">
            <span className="oc-label">{t('audit.from')}</span>
            <input className="oc-input" type="date" value={filters.from}
                   max={filters.to || undefined}
                   onChange={(e) => set({ from: e.target.value })} />
          </label>
          <label className="block">
            <span className="oc-label">{t('audit.to')}</span>
            <input className="oc-input" type="date" value={filters.to}
                   min={filters.from || undefined}
                   onChange={(e) => set({ to: e.target.value })} />
          </label>
        </div>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-ink-muted">{t('audit.dateNote')}</p>
          {hasFilters && (
            <Button size="sm" variant="ghost" onClick={() => set(EMPTY_FILTERS)}>
              {t('common.clearFilters')}
            </Button>
          )}
        </div>
      </Card>

      {rangeInverted && <ErrorNotice message={t('audit.rangeInverted')} />}

      {isLoading ? (
        <div className="space-y-2">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : error ? (
        <ErrorNotice message={describeError(error)} onRetry={() => void refetch()}
                     retryLabel={t('common.retry')} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState icon={<IconShield className="w-10 h-10" />}
                      title={hasFilters ? t('audit.noMatch') : t('audit.none')} />
        </Card>
      ) : (
        <>
          <ul className={cn('space-y-2', isFetching && 'opacity-70 transition-opacity')} aria-busy={isFetching}>
            {items.map((ev) => {
              const open = openId === ev.id;
              const detailId = `audit-detail-${ev.id}`;
              return (
                <li key={ev.id} className="oc-card overflow-hidden">
                  <button
                    className="w-full text-left p-3 sm:p-4 flex items-start gap-3 hover:bg-surface-sunken/60"
                    aria-expanded={open} aria-controls={detailId}
                    onClick={() => setOpenId(open ? null : ev.id)}
                  >
                    <IconChevron className={cn('w-4 h-4 mt-1 shrink-0 text-ink-muted transition-transform',
                      open && 'rotate-90')} />
                    <div className="min-w-0 flex-1 grid gap-x-4 gap-y-1 md:grid-cols-[13rem_minmax(0,1fr)_minmax(0,1fr)]">
                      <time dateTime={ev.createdAt} title={ev.createdAt}
                            className="text-sm text-ink tabular-nums">
                        {format(ev.createdAt)}
                      </time>
                      <p className="text-sm text-ink truncate" title={ev.actorEmail ?? undefined}>
                        {ev.actorEmail ?? <span className="italic text-ink-muted">{t('audit.system')}</span>}
                      </p>
                      <div className="min-w-0">
                        <p className="font-mono text-sm text-ink break-all">{ev.action}</p>
                        {ev.entityType && (
                          <p className="text-xs text-ink-muted break-all">
                            {ev.entityType}{ev.entityId ? ` · ${ev.entityId}` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>

                  {open && (
                    <div id={detailId} className="border-t border-line px-3 sm:px-4 py-3 space-y-3 bg-surface-sunken/40">
                      {ev.changes
                        ? <DiffView changes={ev.changes} />
                        : <p className="oc-muted">{t('audit.noChangesRecorded')}</p>}

                      <dl className="text-xs text-ink-secondary grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5">
                        <dt className="text-ink-muted">{t('audit.eventId')}</dt>
                        <dd className="font-mono break-all">{ev.id}</dd>
                        <dt className="text-ink-muted">{t('audit.timestampUtc')}</dt>
                        <dd className="font-mono">{ev.createdAt}</dd>
                        {ev.ip && (<>
                          <dt className="text-ink-muted">{t('audit.ip')}</dt>
                          <dd className="font-mono">{ev.ip}</dd>
                        </>)}
                      </dl>

                      {/* "Everything that happened to this record" is the
                          follow-up question to every audit finding. */}
                      {ev.entityId && (
                        <Button size="sm" onClick={() => set({ entityId: ev.entityId! })}>
                          {t('audit.viewEntityHistory')}
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          <nav className="flex flex-wrap items-center justify-between gap-2" aria-label={t('audit.pagination')}>
            <p className="oc-muted">
              {t('common.showing')} {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + items.length} {t('common.of')} {total}
            </p>
            <div className="flex gap-2">
              <Button size="sm" disabled={page <= 1} onClick={() => { setPage(page - 1); setOpenId(null); }}>
                {t('common.previous')}
              </Button>
              <Button size="sm" disabled={page >= pageCount} onClick={() => { setPage(page + 1); setOpenId(null); }}>
                {t('common.next')}
              </Button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
}
